/* eslint-disable sonarjs/no-duplicate-string */
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { Popup, type Map, type MapMouseEvent } from 'mapbox-gl';
import { type ComposerTranslation } from 'vue-i18n';

import { isLargeScreen } from '@/helpers/browser';
import { type Culture } from '@/models/iazi/common/enums';
import {
  type ColorValueItem,
  type PriceDistributionData,
} from '@/models/iazi/queries/getPriceDistributionResponse';
import { type MapLayerHighlightName, type MapLayerName, type MapLayerSource } from '@/types';

import {
  CANTON_MAX_ZOOM_DESKTOP,
  CANTON_MAX_ZOOM_MOBILE,
  CANTON_MIN_ZOOM_DESKTOP,
  CANTON_MIN_ZOOM_MOBILE,
  DEFAULT_MAX_ZOOM,
  DEFAULT_MIN_ZOOM,
  FALLBACKCOLOR,
  FILLOUTLINECOLOR,
  LINECOLOR_HOVER,
  LINECOLOR_SELECTED,
  MUNICIPALITY_MAX_ZOOM_DESKTOP,
  MUNICIPALITY_MAX_ZOOM_MOBILE,
  MUNICIPALITY_MIN_ZOOM_DESKTOP,
  MUNICIPALITY_MIN_ZOOM_MOBILE,
  OPACITY_MAX,
  TOWN_MAX_ZOOM_DESKTOP,
  TOWN_MAX_ZOOM_MOBILE,
  TOWN_MIN_ZOOM_DESKTOP,
  TOWN_MIN_ZOOM_MOBILE,
} from './constants';

const LAYERS_CULTURE_DEPENDENT = [
  'country-label',
  'state-label',
  'settlement-major-label',
  'settlement-minor-label',
  'settlement-subdivision-label',
];
const LAYER_ID_BASE = 'national-park';

export const clearHighlightLayer = (map: Map): void => {
  const highlightSourceId = 'highlightFeatureSelected';
  const highlightLayerId = 'highlightLayerSelected';

  if (map.getLayer(highlightLayerId)) {
    map.removeLayer(highlightLayerId);
  }

  if (map.getSource(highlightSourceId)) {
    map.removeSource(highlightSourceId);
  }
};
export const addHighlightLayer = (map: Map, featureData: unknown): void => {
  const highlightSourceId = 'highlightFeatureSelected';
  const highlightLayerId = 'highlightLayerSelected';

  clearHighlightLayer(map);

  map.addSource(highlightSourceId, {
    type: 'geojson',
    data: featureData as GeoJSON.FeatureCollection,
  });

  // Add a line outline around the polygon.
  map.addLayer(
    {
      id: highlightLayerId,
      type: 'line',
      source: highlightSourceId,
      layout: {},
      paint: {
        'line-color': LINECOLOR_SELECTED,
        'line-width': 2,
      },
    },
    'settlement-subdivision-label',
  );
};
export const setLayerVisibility = (
  map: Map,
  layers: Array<MapLayerName | MapLayerHighlightName>,
  isVisible: boolean,
): void => {
  const visibility = isVisible ? 'visible' : 'none';
  layers.forEach((element: MapLayerName | MapLayerHighlightName) => {
    map.setLayoutProperty(element, 'visibility', visibility);
  });
};
export const layerHighliter = (prop: {
  map: Map;
  source: MapLayerSource;
  layer: MapLayerName;
  layerHighlightName: MapLayerHighlightName;
  priceData: PriceDistributionData | null;
  popup: Popup;
  culture: Culture;
  insufficientData: string;
}): void => {
  let hoverStateId: any = null;
  let lastFeatureId: any = null; // Performance impact mitigation over mousemove, to check if mouseover can be used.

  // add user Events to cantons layer
  prop.map.on('mousemove', prop.layer, (e: MapMouseEvent) => {
    const currentFeatureId = e.features?.[0]?.id;
    if (currentFeatureId === lastFeatureId) {
      // If the mouse moved but is still on the same feature, do nothing
      return;
    }
    // Update the lastFeatureId with the current feature ID
    lastFeatureId = currentFeatureId;

    prop.map.getCanvas().style.cursor = 'pointer';

    if (hoverStateId !== null) {
      prop.map.setFeatureState(
        {
          source: prop.source,
          sourceLayer: prop.layerHighlightName,
          id: hoverStateId,
        },
        { hover: false },
      );
    }
    hoverStateId = e.features?.[0]?.id;
    prop.map.setFeatureState(
      {
        source: prop.source,
        sourceLayer: prop.layerHighlightName,
        id: hoverStateId,
      },
      { hover: true },
    );

    if (prop.popup) {
      prop.popup.remove();
    }
    const priceValue = e.features?.[0]?.state?.price;
    prop.popup
      .setLngLat(e.lngLat)
      .setHTML(
        `
        <div><b> ${e.features?.[0]?.state?.geoName}<b></div>
        <div>${
          priceValue !== 0 && priceValue !== undefined
            ? new Intl.NumberFormat(prop.culture, {
                style: 'currency',
                currency: 'CHF',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(Number(priceValue)) + '/m<sup>2</sup>'
            : prop.insufficientData
        }</div>
        `,
      )
      .addTo(prop.map);
  });
  prop.popup.on('open', () => {
    const popupContainer = prop.popup.getElement();

    if (!popupContainer) {
      return;
    }

    popupContainer.addEventListener(
      'wheel',
      (event: { ctrlKey: any; metaKey: any; preventDefault: () => void }) => {
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
        }
      },
      { passive: false },
    );
  });

  prop.map.on('mouseleave', prop.layer, () => {
    prop.map.getCanvas().style.cursor = '';
    prop.popup.remove();

    if (hoverStateId !== null) {
      prop.map.setFeatureState(
        {
          source: prop.source,
          sourceLayer: prop.layerHighlightName,
          id: hoverStateId,
        },
        { hover: false },
      );
    }

    hoverStateId = null;
    lastFeatureId = null;
  });
};
export const HoverHighlightLayer = (
  map: Map,
  tileSetNameHighlightName: MapLayerHighlightName,
  layerSource: MapLayerSource,
): void => {
  // eslint-disable-next-line sonarjs/no-duplicate-string
  map.addLayer({
    id: tileSetNameHighlightName,
    type: 'line',
    source: layerSource,
    'source-layer': tileSetNameHighlightName,
    layout: {},
    paint: {
      'line-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        LINECOLOR_HOVER,
        LINECOLOR_HOVER,
      ],
      'line-width': 2,
      'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0],
    },
  });
};

export const setInitialPaintProperties = (
  map: Map,
  lang: string = HgSupportedLanguage.DE,
): void => {
  LAYERS_CULTURE_DEPENDENT.forEach((layerId) => {
    const layerExists = (map.getStyle().layers || []).some((layer) => layer.id === layerId);
    if (layerExists) {
      (map as Map).setLayoutProperty(layerId, 'text-field', ['get', `name_${lang}`]);
    }
  });
};
interface AddLayerParams {
  map: Map | null;
  tileSetName: MapLayerName;
  layerSource: MapLayerSource;
  municipalityTileSetName: MapLayerName;
  townTileSetName: MapLayerName;
  cantonTileSetName: MapLayerName;
}
export function addLayer(params: AddLayerParams): void {
  const {
    map,
    tileSetName,
    layerSource,
    municipalityTileSetName,
    townTileSetName,
    cantonTileSetName,
  } = params;

  const zoomConfigDesktop = {
    [cantonTileSetName]: { minzoom: CANTON_MIN_ZOOM_DESKTOP, maxzoom: CANTON_MAX_ZOOM_DESKTOP },
    [municipalityTileSetName]: {
      minzoom: MUNICIPALITY_MIN_ZOOM_DESKTOP,
      maxzoom: MUNICIPALITY_MAX_ZOOM_DESKTOP,
    },
    [townTileSetName]: { minzoom: TOWN_MIN_ZOOM_DESKTOP, maxzoom: TOWN_MAX_ZOOM_DESKTOP },
    default: { minzoom: DEFAULT_MIN_ZOOM, maxzoom: DEFAULT_MAX_ZOOM },
  };
  const zoomConfigMobile = {
    [cantonTileSetName]: { minzoom: CANTON_MIN_ZOOM_MOBILE, maxzoom: CANTON_MAX_ZOOM_MOBILE },
    [municipalityTileSetName]: {
      minzoom: MUNICIPALITY_MIN_ZOOM_MOBILE,
      maxzoom: MUNICIPALITY_MAX_ZOOM_MOBILE,
    },
    [townTileSetName]: { minzoom: TOWN_MIN_ZOOM_MOBILE, maxzoom: TOWN_MAX_ZOOM_MOBILE },
    default: { minzoom: DEFAULT_MIN_ZOOM, maxzoom: DEFAULT_MAX_ZOOM },
  };

  const { minzoom, maxzoom } =
    (isLargeScreen() ? zoomConfigDesktop[tileSetName] : zoomConfigMobile[tileSetName]) ||
    zoomConfigDesktop.default;
  map?.addLayer(
    {
      id: tileSetName,
      type: 'fill',
      source: layerSource,
      'source-layer': tileSetName,
      minzoom: minzoom,
      maxzoom: maxzoom,
      layout: {},
      paint: {
        'fill-outline-color': FILLOUTLINECOLOR,
        'fill-opacity': OPACITY_MAX,
        'fill-color': FALLBACKCOLOR,
      },
    },
    LAYER_ID_BASE,
  );
}

export function dateFilter(dateValue: number | null): string {
  if (!dateValue) {
    return '';
  }
  const year = dateValue.toString().slice(0, 4);
  const month = dateValue.toString().slice(4, 6);
  const day = dateValue.toString().slice(6, 8);
  return `${day}.${month}.${year}`;
}

export const calculateEvolution = (factor: number, factorValue: number): number => {
  return (factorValue / factor - 1) * 100;
};

export const getValuationFunnelUrl = (t: ComposerTranslation): string =>
  `${import.meta.env.VUE_PLATFORM_BASE_URL}/${t('common.propertyValuationLink')}?source=marketAtlas`;

export const sortLegendColors = (
  colorValues: Array<ColorValueItem> | undefined | null,
): Array<string> | null => {
  if (colorValues) {
    return colorValues?.sort((a, b) => a.sortOrder - b.sortOrder).map((obj) => obj.colorValue);
  } else {
    return null;
  }
};
